import { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import {
  Chart,
  ArgumentAxis,
  BarSeries,
  ValueAxis,
  LineSeries,
  Title,
  Legend,
  Tooltip,
} from "@devexpress/dx-react-chart-material-ui";
import { styled } from "@mui/material/styles";
import { Animation } from "@devexpress/dx-react-chart";
import { BASE_URL } from "../services/client/rest";
import { EventTracker } from "@devexpress/dx-react-chart";
import DashboardApi from "../services/API/Dashboard.Api";
import { useQuery } from "react-query";
import moment from "moment";

const PREFIX = "Demo";

const classes = {
  chart: `${PREFIX}-chart`,
};

const format = () => (tick: any) => tick;

const Root = (props: any) => (
  <Legend.Root
    {...props}
    sx={{ display: "flex", margin: "auto", flexDirection: "row" }}
  />
);
const Label = (props: any) => (
  <Legend.Label sx={{ pt: 1, whiteSpace: "nowrap" }} {...props} />
);
const Item = (props: any) => (
  <Legend.Item sx={{ flexDirection: "column" }} {...props} />
);

const ValueLabel = (props: any) => {
  const { text } = props;
  return <ValueAxis.Label {...props} text={`${text}`} />;
};

const TitleText = (props: any) => (
  <Title.Text {...props} sx={{ whiteSpace: "pre" }} />
);

const StyledChart = styled(Chart)(() => ({
  [`&.${classes.chart}`]: {
    paddingRight: "20px",
  },
}));

export default function Dashboard() {
  const [chartData, SetChartData] = useState([] as any);
  const [disconnectedChart, SetDisconnectedChart] = useState([] as any);
  const [aspenChartData, SetAspenChartData] = useState([] as any);
  const [formattedData, setFormattedData] = useState([] as any);

  var bearer = "Bearer " + localStorage.getItem("token");

  useEffect(() => {
    ActiveUserGraph();
    AspenEndpointsGraph();
    DisconnectedUserGraph();
  }, []);

  function ActiveUserGraph() {
    let url = BASE_URL + "Dashboard";

    fetch(url, {
      method: "get",
      headers: new Headers({
        Authorization: bearer,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        SetChartData(data.data);
      })
      .catch();
  }

  //!----------------- useQuery for Fetching MaxUserData -----------------//
  const { data: maxUserData } = useQuery(
    ["GetMaxUserDataGraph"],
    () => DashboardApi.getMaxUserDataGraph(),
    {
      initialData: [],
      refetchInterval: 1000 * 60 * 5, // 5 min
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
    }
  );

  //!----------------- useQuery for GetAspenGraph Data -----------------//
  const { data: aspenGraphData } = useQuery(
    ["GetAspenGraph"],
    () => DashboardApi.getAspenGraph(),
    {
      initialData: [],
      refetchInterval: 1000 * 60 * 5, // 5 min
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    let newData = maxUserData.map((ele: any) => {
      let day = moment(ele.day).format("DD");
      return { ...ele, day };
    });
    setFormattedData([...newData]);
  }, [maxUserData]);

  function DisconnectedUserGraph() {
    let url = BASE_URL + "Dashboard/DisconnectedGraph";

    fetch(url, {
      method: "get",
      headers: new Headers({
        Authorization: bearer,
      }),
    })
      .then((response) => response.json())
      .then((data) => SetDisconnectedChart(data.data))
      .catch();
  }

  function AspenEndpointsGraph() {
    let url = BASE_URL + "Dashboard/AspenDashboard";

    fetch(url, {
      method: "get",
      headers: new Headers({
        Authorization: bearer,
      }),
    })
      .then((response) => response.json())
      .then((data) => SetAspenChartData(data.data))
      .catch();
  }

  return (
    <Paper style={{ paddingLeft: "100px", paddingRight: "100px" }}>
      {/********** Active Users Chart **********/}
      <StyledChart data={chartData}>
        <ArgumentAxis tickFormat={format} />
        <ValueAxis labelComponent={ValueLabel} />

        <LineSeries
          name="ActiveUsers"
          valueField="value"
          argumentField="hour"
        />
        <Legend
          position="bottom"
          rootComponent={Root}
          itemComponent={Item}
          labelComponent={Label}
        />
        <Title
          text={`Active Users Statistics ${"\n"}(Azure VM's)`}
          textComponent={TitleText}
        />
        <Animation />
        <EventTracker />
        <Tooltip />
      </StyledChart>

      {/********** Max number of users each day for 30 days **********/}
      <Chart data={formattedData}>
        <ArgumentAxis />
        <ValueAxis />

        <BarSeries
          name="Max Number of ActiveUsers"
          valueField="maxUsers"
          argumentField="day"
        />

        <Legend
          position="bottom"
          rootComponent={Root}
          itemComponent={Item}
          labelComponent={Label}
        />

        <Title
          text={`Max Number of Active Users ${"\n"}(Azure VM's)`}
          textComponent={TitleText}
        />
        <Animation />
        <EventTracker />
        <Tooltip />
      </Chart>

      {/********** ASPEN Endpoints Users Chart **********/}
      <StyledChart data={aspenGraphData}>
        <ArgumentAxis tickFormat={format} />
        <ValueAxis labelComponent={ValueLabel} />

        <LineSeries
          name="CurrentConnections"
          valueField="currentConnections"
          argumentField="hour"
        />
        <LineSeries
          name="AnonymousUsers"
          valueField="anonymousUsers"
          argumentField="hour"
        />

        <Legend
          position="bottom"
          rootComponent={Root}
          itemComponent={Item}
          labelComponent={Label}
        />
        <Title text={`ASPEN Endpoints Users`} textComponent={TitleText} />
        <Animation />
        <EventTracker />
        <Tooltip />
      </StyledChart>

      {/********** ASPEN Helth Check Chart  **********/}
      <StyledChart data={aspenChartData}>
        <ArgumentAxis tickFormat={format} />
        <ValueAxis labelComponent={ValueLabel} />

        <LineSeries name="ASPENAPI" valueField="value1" argumentField="hour" />
        <LineSeries name="IDENTITY" valueField="value2" argumentField="hour" />
        <LineSeries
          name="ASPENCONNECTOR"
          valueField="value3"
          argumentField="hour"
        />
        <LineSeries
          name="ASPENCOMMUNITY"
          valueField="value4"
          argumentField="hour"
        />
        <LineSeries name="ASPEN" valueField="value5" argumentField="hour" />
        <LineSeries
          name="ASPENSERVICES"
          valueField="value6"
          argumentField="hour"
        />
        <Legend
          position="bottom"
          rootComponent={Root}
          itemComponent={Item}
          labelComponent={Label}
        />
        <Title
          text={`Aspen Endpoints Health Check`}
          textComponent={TitleText}
        />
        <Animation />
        <EventTracker />
        <Tooltip />
      </StyledChart>

      <StyledChart data={disconnectedChart}>
        <ArgumentAxis tickFormat={format} />
        <ValueAxis labelComponent={ValueLabel} />

        <LineSeries
          name="DisconnectedUsers"
          valueField="value"
          argumentField="hour"
        />
        <Legend
          position="bottom"
          rootComponent={Root}
          itemComponent={Item}
          labelComponent={Label}
        />
        <Title
          text={`Disconnected Users Statistics ${"\n"}(Azure VM's)`}
          textComponent={TitleText}
        />
        <Animation />
        <EventTracker />
        <Tooltip />
      </StyledChart>
    </Paper>
  );
}
